'use client';
import React from 'react';
import { ExternalLink, Shield, X, AlertCircle } from 'lucide-react';

interface DisclaimerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  buyUrl: string;
  insurerName: string;
}

export default function DisclaimerPopup({ isOpen, onClose, buyUrl, insurerName }: DisclaimerPopupProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 animate-slide-up">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 mb-4">
          <AlertCircle className="w-6 h-6 text-amber-600" />
        </div>

        {/* Title */}
        <h2 className="text-lg font-extrabold text-foreground mb-2">Before You Continue</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          You are about to be redirected to <strong className="text-foreground">{insurerName}&apos;s official website</strong> to complete your purchase.
        </p>

        {/* Disclaimer box */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-5">
          <div className="flex items-start gap-2.5">
            <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800 mb-1">Important Disclaimer</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                MediCompare is an independent informational platform only. We do <strong>not</strong> sell insurance, collect payments, or process policy applications. All purchases are made directly on the insurer&apos;s official website. Please verify all policy terms and conditions with the insurer before purchasing.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2.5">
          <a
            href={buyUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="btn-primary w-full justify-center py-3 text-sm"
          >
            Continue to {insurerName}
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={onClose}
            className="btn-outline w-full justify-center py-2.5 text-sm"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}
